import { Component } from "@theme/component";
import { debounce, onAnimationEnd, prefersReducedMotion } from "@theme/utilities";
import { sectionRenderer } from "@theme/section-renderer";
import { morph } from "@theme/morph";
import { RecentlyViewed } from "@theme/recently-viewed-products";
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from "@theme/dialog";

/** Products shown in the live results; matches the default `limit` of predictive-search-products-list. */
const PREDICTIVE_SEARCH_PRODUCT_LIMIT = 8;

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ["searchInput", "predictiveSearchResults", "resetButton"];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  #emptyStateLoaded = false;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest("dialog-component");
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    if (dialog) {
      document.addEventListener("keydown", this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });
      dialog.addEventListener(DialogOpenEvent.eventName, this.#handleDialogOpen, { signal, once: true });

      this.addEventListener("click", this.#handleModalClick, { signal });
    }

    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        this.#loadEmptyState();
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest("button") ||
      target.closest("a") ||
      target.closest("input");

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === "k") {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  #handleDialogOpen = () => {
    if (!this.#emptyStateLoaded && RecentlyViewed.getProducts().length > 0) {
      this.#loadEmptyState();
    }
  };

  #loadEmptyState() {
    if (this.#emptyStateLoaded) return;
    this.#emptyStateLoaded = true;
    this.resetSearch(false);
  }

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        ".predictive-search-results__wrapper-queries, " +
          ".predictive-search-results__wrapper-products, " +
          ".predictive-search-results__list",
      ),
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains("predictive-search-results__wrapper-products")) {
          return Array.from(container.querySelectorAll(".predictive-search-results__card"));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute("aria-selected") === "true") ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    let activeItem = null;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove("keyboard-focus");
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute("aria-selected", "true");
        if (this.#isKeyboardNavigation) {
          item.classList.add("keyboard-focus");
        }
        activeItem = item;
      } else {
        item.removeAttribute("aria-selected");
      }
    }

    activeItem?.scrollIntoView({ behavior: prefersReducedMotion() ? "instant" : "smooth", block: "nearest" });
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === "Escape") {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === "ArrowLeft" || event.key === "ArrowRight") {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case "ArrowDown":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case "Tab":
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case "ArrowUp":
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case "Enter": {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector("[data-single-result-url]");
        if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector("a")?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set("q", this.refs.searchInput.value);
          searchUrl.searchParams.set("type", "article");
          searchUrl.searchParams.set("options[prefix]", "last");
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add("removing");

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.#resetSearch();
      return;
    }

    this.#showResetButton();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      this.refs.predictiveSearchResults.querySelector(".predictive-search-results__inner")?.scrollTo(0, 0);
      this.querySelector(".predictive-search-form__content")?.scrollTo(0, 0);
    });
  }

  /**
   * Fetch storefront search results using the section renderer and update the results container.
   *
   * The request is split per resource type: Shopify returns at most 2 articles whenever `product`
   * and `article` share one `/search` request (measured 2026-10: "bourbon" = 59 articles via
   * `type=article`, 5 products, but only 7 results via `type=product,article`). `type=article` alone
   * is uncapped, so articles and products are fetched as two parallel section renders and composed
   * into one document off-DOM, then applied with a single morph (no flicker). The composed markup
   * keeps the same group order the Liquid renders for a mixed request:
   * Recipes -> Products -> News & Events.
   *
   * The storefront product search is FUZZY on this store (measured 2026-10: "zzqqxxyy" = 5 products,
   * "sweatshirt" = 10, "bourbon" = 8 with one real match), while the Predictive Search API is strict
   * (0, 0 and 1 respectively). So a third, parallel request asks the Predictive API which products
   * really match, and the product render is filtered and reordered to that set before composing.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    const { sectionId } = this.dataset;
    if (!sectionId) return;

    const { predictiveSearchResults } = this.refs;
    const abortController = this.#createAbortController();

    /** @type {[string, string, string[] | null]} */
    let results;
    try {
      results = await Promise.all([
        sectionRenderer.getSectionHTML(sectionId, false, this.#buildSearchUrl(searchTerm, "article")),
        sectionRenderer.getSectionHTML(sectionId, false, this.#buildSearchUrl(searchTerm, "product")),
        this.#fetchMatchingProductIds(searchTerm, abortController.signal),
      ]);
    } catch (error) {
      if (abortController.signal.aborted) return;
      throw error;
    }

    if (abortController.signal.aborted) return;

    const composedRoot = this.#composeSearchResults(...results);
    if (!composedRoot) return;

    // morph() is children-only: wrap the composed root so #predictive-search-results itself lands in the DOM.
    const container = document.createElement("div");
    container.append(composedRoot);
    morph(predictiveSearchResults, container);
    this.#resetScrollPositions();
  }

  /**
   * Build a storefront search URL for a single resource type.
   * @param {string} searchTerm - The term to search for
   * @param {"article" | "product"} type - The resource type to search
   * @returns {URL} The search URL
   */
  #buildSearchUrl(searchTerm, type) {
    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set("q", searchTerm);
    url.searchParams.set("type", type);
    url.searchParams.set("options[prefix]", "last");
    return url;
  }

  /**
   * Ask the Predictive Search API which products truly match the term.
   * The storefront `/search?type=product` request is fuzzy on this store (nonsense terms return
   * products), the Predictive API is strict, so its result set is the source of truth for which
   * product cards to show and in what order.
   * The `.json` suffix is required: the bare route is the HTML section-render endpoint and 422s.
   * @param {string} searchTerm - The term to search for
   * @param {AbortSignal} [signal] - Aborts the request when a newer search starts
   * @returns {Promise<string[] | null>} Matching product ids in the API's order, or null when the
   *   request fails (callers then keep the storefront render unfiltered)
   */
  async #fetchMatchingProductIds(searchTerm, signal) {
    try {
      const url = new URL(`${Theme.routes.predictive_search_url}.json`, location.origin);
      url.searchParams.set("q", searchTerm);
      url.searchParams.set("resources[type]", "product");
      url.searchParams.set("resources[limit]", "10");
      url.searchParams.set("resources[options][fields]", "title,product_type,variants.title,vendor,tag,body");
      url.searchParams.set("resources[options][prefix]", "last");

      const response = await fetch(url, { signal });
      if (!response.ok) return null;

      const data = await response.json();
      const products = data?.resources?.results?.products;
      if (!Array.isArray(products)) return null;

      return products.map(/** @param {{ id: number | string }} product */ (product) => String(product.id));
    } catch {
      return null;
    }
  }

  /**
   * Keep only the product cards the Predictive API confirmed, reordered to the API's order and
   * trimmed to the snippet's display limit. Cards the API did not return are removed.
   * With `productIds` null (API failed) every card is kept in render order, still trimmed, since
   * the live-results render emits up to 24 cards for this filter.
   * Each card's product id is read from the `<li>` itself (`data-product-id`, emitted by
   * `snippets/predictive-search-products-list.liquid`), falling back to any descendant carrying
   * one. `resource-card` only emits an id when `settings.transition_to_main_product` is on.
   * @param {Element} productsGroup - The `#predictive-search-products` group of the product render
   * @param {string[] | null} productIds - Matching product ids in the API's order, or null
   * @returns {HTMLElement[]} The kept cards, in display order
   */
  #filterProductCards(productsGroup, productIds) {
    const list = productsGroup.querySelector(".predictive-search-results__list");
    if (!list) return [];

    /** @type {Map<string, HTMLElement>} */
    const cardsById = new Map();
    for (const card of list.querySelectorAll("li.predictive-search-results__card--product")) {
      if (!(card instanceof HTMLElement)) continue;
      const id = card.dataset.productId ?? card.querySelector("[data-product-id]")?.getAttribute("data-product-id");
      if (id && !cardsById.has(id)) cardsById.set(id, card);
      card.remove();
    }

    const orderedCards = productIds
      ? productIds.map((id) => cardsById.get(id)).filter((card) => card instanceof HTMLElement)
      : [...cardsById.values()];
    const keptCards = orderedCards.slice(0, PREDICTIVE_SEARCH_PRODUCT_LIMIT);

    list.append(...keptCards);
    return keptCards;
  }

  /**
   * Merge the product-only section render into the article-only one.
   * The article document is the base (it carries the Recipes and News & Events groups, the
   * no-results copy and the live status); the Products group is slotted between the two article
   * groups, and the result count, live status and single-result marker are recomputed for the
   * combined set.
   *
   * When `productIds` is an array (the Predictive API answered), the product cards are filtered to
   * that set first, because the storefront product search behind the product render is fuzzy. When
   * it is null (the API failed), the product render is used unfiltered.
   * @param {string} articleMarkup - Section HTML for `type=article`
   * @param {string} productMarkup - Section HTML for `type=product`
   * @param {string[] | null} productIds - Product ids confirmed by the Predictive API, or null
   * @returns {HTMLElement | null} The composed `#predictive-search-results` element, or null if absent
   */
  #composeSearchResults(articleMarkup, productMarkup, productIds) {
    const parser = new DOMParser();
    const articleDoc = parser.parseFromString(articleMarkup ?? "", "text/html");
    const productDoc = parser.parseFromString(productMarkup ?? "", "text/html");

    const articleRoot = articleDoc.querySelector("#predictive-search-results");
    if (!(articleRoot instanceof HTMLElement)) return null;

    const inner = articleRoot.querySelector(".predictive-search-results__inner");
    if (!inner) return null;

    const productRoot = productDoc.querySelector("#predictive-search-results");
    const isFiltered = Array.isArray(productIds);
    let productsGroup = productDoc.querySelector("#predictive-search-products");

    /** @type {HTMLElement[]} */
    let keptCards = [];
    if (productsGroup) {
      keptCards = this.#filterProductCards(productsGroup, productIds);
      if (isFiltered && keptCards.length === 0) productsGroup = null;
    }

    if (productsGroup) {
      inner.querySelector(".predictive-search-results__no-results")?.remove();

      const recipesGroup = inner.querySelector(".predictive-search-results__recipes");
      const blogPostsGroup = inner.querySelector(".predictive-search-results__blog-posts");

      if (recipesGroup) {
        recipesGroup.after(productsGroup);
      } else if (blogPostsGroup) {
        blogPostsGroup.before(productsGroup);
      } else {
        inner.append(productsGroup);
      }
    }

    const articleCount = Number(articleRoot.dataset.resultsCount || 0);
    // Filtered: count the KEPT cards; the product render's data-results-count is the fuzzy count.
    const unfilteredProductCount =
      productRoot instanceof HTMLElement ? Number(productRoot.dataset.resultsCount || 0) : 0;
    const productCount = isFiltered ? keptCards.length : unfilteredProductCount;
    const total = articleCount + productCount;

    // Each render emits its own single-result marker based on ITS count; only the combined total matters.
    // A filtered product render's marker reflects the fuzzy count, so it is rebuilt from the kept card.
    const productMarker = isFiltered
      ? this.#createSingleResultMarker(keptCards[0])
      : productDoc.querySelector("[data-single-result-url]");
    const singleResultMarker = articleDoc.querySelector("[data-single-result-url]") ?? productMarker;
    for (const marker of [
      ...articleDoc.querySelectorAll("[data-single-result-url]"),
      ...productDoc.querySelectorAll("[data-single-result-url]"),
    ]) {
      marker.remove();
    }
    if (total === 1 && singleResultMarker) {
      inner.append(singleResultMarker);
    }

    // With no products the article render's status already reads correctly (incl. no-results wording).
    if (productCount > 0) {
      const status = inner.querySelector("[data-results-status]");
      if (status instanceof HTMLElement && status.dataset.statusTemplate) {
        status.textContent = status.dataset.statusTemplate.replace("__COUNT__", String(total));
      }
    }

    articleRoot.dataset.resultsCount = String(total);

    return articleRoot;
  }

  /**
   * Build a single-result marker (`[data-single-result-url]`) from a product card's link.
   * @param {HTMLElement | undefined} card - A kept product card
   * @returns {HTMLElement | null} The marker, or null when the card has no link
   */
  #createSingleResultMarker(card) {
    const href = card?.querySelector("a.resource-card__link")?.getAttribute("href");
    if (!href) return null;

    const marker = document.createElement("div");
    marker.dataset.singleResultUrl = href;
    return marker;
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set("q", viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(" OR "));
    url.searchParams.set("type", "product");

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = true;
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    resetButton.hidden = false;
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = "predictive-search-empty";

    this.#currentIndex = -1;
    searchInput.value = "";
    this.#hideResetButton();

    const abortController = this.#createAbortController();
    const url = new URL(window.location.href);
    url.searchParams.delete("page");

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, "text/html")
      .querySelector(".predictive-search-empty-section");

    if (!parsedEmptySectionMarkup) throw new Error("No empty section markup found");

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, "text/html");
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById("predictive-search-products");
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute("ref", "recentlyViewedWrapper");
        }
      }

      const innerElement = parsedEmptySectionMarkup.querySelector(".predictive-search-results__inner");
      if (!innerElement) return;
      innerElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#resetScrollPositions();
  };
}

if (!customElements.get("predictive-search-component")) {
  customElements.define("predictive-search-component", PredictiveSearchComponent);
}
